import type { KeyboardEvent } from 'react';
import { MONTHS } from './UiDatePicker.constants';
import type { CalendarGridProps } from './UiDatePicker.types';
import { addMonths, chunk, clampToRange, fromIso, isMonthInRange, withMonth } from './UiDatePicker.utils';
import { UiDatePickerCell } from './UiDatePickerCell';

type UiDatePickerMonthsProps = CalendarGridProps & {
    /** Picks the month; the calendar returns to its days. */
    onSelect: (iso: string) => void;
};

/** Grid keys move a month sideways and a row (three months) up or down. */
const MONTH_STEPS: Record<string, number> = {
    ArrowLeft: -1,
    ArrowRight: 1,
    ArrowUp: -3,
    ArrowDown: 3,
};

/** The twelve months of the focused year. */
export function UiDatePickerMonths({ focus, value, range, onFocusChange, onSelect }: UiDatePickerMonthsProps) {
    const date = fromIso(focus);
    const year = date.getFullYear();
    const selected = value ? fromIso(value) : null;
    const now = new Date();
    const rows = chunk(
        MONTHS.map((month, index) => ({ ...month, index })),
        3,
    );

    const handleKeyDown = (event: KeyboardEvent) => {
        const step = MONTH_STEPS[event.key];
        if (step === undefined) return;

        event.preventDefault();
        onFocusChange(clampToRange(addMonths(focus, step), range), true);
    };

    return (
        <div
            role="grid"
            aria-label={String(year)}
            className="form-datepicker-grid"
            data-columns="3"
            onKeyDown={handleKeyDown}
        >
            {rows.map((row) => (
                <div key={row[0].index} role="row" className="form-datepicker-row">
                    {row.map((month) => (
                        <span key={month.index} role="gridcell">
                            <UiDatePickerCell
                                label={`${month.long} ${year}`}
                                focused={month.index === date.getMonth()}
                                selected={selected?.getFullYear() === year && selected.getMonth() === month.index}
                                current={now.getFullYear() === year && now.getMonth() === month.index}
                                disabled={!isMonthInRange(year, month.index, range)}
                                onClick={() => onSelect(clampToRange(withMonth(focus, month.index), range))}
                            >
                                {month.short}
                            </UiDatePickerCell>
                        </span>
                    ))}
                </div>
            ))}
        </div>
    );
}
